import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navigation, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { phaseColor, phaseBadge } from "@/lib/phases";

export const NearbySuggestions = ({ slug }) => {
  const { user, isFavorite, toggleFavorite } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems([]);
    api.get(`/pyramids/${slug}/nearby`, { params: { limit: 6 } }).then((r) => setItems(r.data || [])).catch(() => {});
  }, [slug]);

  const quickSave = async (e, it) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Sign in to save favorites", { action: { label: "Sign in", onClick: () => navigate("/account") } });
      return;
    }
    try {
      const r = await toggleFavorite(it.slug);
      toast.success(r.favorited ? `Saved “${it.name}” for your trip` : `Removed “${it.name}”`);
    } catch { toast.error("Something went wrong"); }
  };

  if (items.length === 0) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 pb-12" data-testid="nearby-suggestions">
      <div className="border-t border-border pt-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-1 flex items-center gap-1.5">
          <Navigation className="h-3 w-3" /> Nearby
        </p>
        <h3 className="font-serif text-2xl font-semibold text-foreground">Add these to the same trip</h3>
        <p className="mt-1 text-sm text-muted-foreground">Other mysteries closest to this one — tap the star to save them to your map.</p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => {
            const color = phaseColor(it.phase);
            const fav = isFavorite(it.slug);
            return (
              <Link
                key={it.slug}
                to={`/pyramid/${it.slug}`}
                data-testid={`nearby-card-${it.slug}`}
                className="group relative flex gap-3 border border-border bg-card p-3 hover:border-accent hover:-translate-y-0.5 transition-all"
              >
                {it.image ? (
                  <img src={it.image} alt="" loading="lazy" className="h-16 w-16 shrink-0 object-cover border border-border" />
                ) : (
                  <span className="h-16 w-16 shrink-0 border border-border" style={{ background: `${color}22` }} />
                )}
                <div className="min-w-0 flex-1 pr-7">
                  <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 text-white" style={{ background: color }}>{phaseBadge(it.phase)}</span>
                  <h4 className="mt-1 font-serif text-base font-semibold text-foreground leading-tight group-hover:text-accent">{it.name}</h4>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                    {it.country} · <span className="text-accent">{it.distance_km.toLocaleString()} km</span>
                  </p>
                </div>
                <button
                  data-testid={`nearby-fav-${it.slug}`}
                  onClick={(e) => quickSave(e, it)}
                  title={fav ? "Saved" : "Save for your trip"}
                  className="absolute top-2 right-2 p-1.5 transition-colors"
                >
                  <Star className={`h-4 w-4 ${fav ? "fill-accent text-accent" : "text-muted-foreground hover:text-accent"}`} />
                </button>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
